import React, { ReactElement, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  Auth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateEmail,
  updatePassword,
  User,
  ActionCodeSettings,
} from 'firebase/auth';
import { disableNetwork, enableNetwork, doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { StorageReference } from 'firebase/storage';

import { useAuthState } from 'react-firebase-hooks/auth';

import { logAnalyticsEvent, setAnalyticsUserId } from '../analytics/analytics';
import { auth, cloudFunctions, firestore, filesRef } from '../firebase';
import SuperUser from '../types/SuperUser';
import { isValidDocumentId } from '../utils/utils';

export interface AuthProviderProps {
  children?: ReactNode;
}

export interface AuthUser {
  user: User | null;
  profileId: string | null | undefined;
}

export interface AuthContextModel {
  auth: Auth;
  authUrl: string;
  checkUserExists: (email: string) => Promise<boolean>;
  filesRef: StorageReference;
  impersonating: string;
  isAuthGod: boolean;
  isEmailVerified: boolean;
  isGod: boolean;
  isLoggedIn: boolean;
  isLoggingIn: boolean;
  user: User | null;
  resetPassword: (email: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
  authUser: AuthUser | null | undefined;
  updateUserEmail: (newEmail: string) => Promise<void>;
  updateUserPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  reauthenticateUser: (password: string) => Promise<void>;
  sendVerifyEmailChange: (continueUrl: string) => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextModel>({} as AuthContextModel);

export function useAuth(): AuthContextModel {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }: AuthProviderProps): ReactElement => {
  // TODO: Handle user auth error
  const [user, isLoggingIn, isLoginError] = useAuthState(auth);

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isAuthGod, setIsAuthGod] = useState<boolean>(false);
  const [isGod, setIsGod] = useState<boolean>(false);
  const [impersonating, setImpersonating] = useState<string>('');
  const [authUser, setAuthUser] = useState<AuthUser | null | undefined>(null);
  const lastClaimUpdatedAtRef = useRef<number | null>(null);
  const authUrl = 'https://gratigolf.com'; // Used for the auth action handlers.

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        console.log("User loaded.")
        setIsLoggedIn(true);
        user.getIdTokenResult().then((tokenResult) => {
          if (tokenResult?.claims?.level === 'god') {
            setIsAuthGod(true);
            console.log('Auth god detected - setting authUser with own profileId, superUser listener may update for impersonation');
            // Set authUser immediately so DataProvider doesn't think user is logged out
            // The superUser listener will update profileId if impersonating
            const profileId = (tokenResult?.claims?.profileId as string) || user.uid;
            setAnalyticsUserId(profileId);
            setAuthUser({ user: user, profileId: profileId });
          } else {
            setIsAuthGod(false);
            setIsGod(false);
            // Set userProfileId from claim or fall back to auth UID
            const profileId = (tokenResult?.claims?.profileId as string) || user.uid;
            console.log('profileId: ', profileId);
            console.log('user.uid: ', user.uid);
            setAnalyticsUserId(profileId);
            setAuthUser({user: user, profileId: profileId});
          }
        }).catch((error) => {
          console.error("Error getting token result:", error);
          // Fallback to using auth UID if token retrieval fails
          const fallbackId = user.uid;
          setAuthUser({user: user, profileId: fallbackId});
        });
      } else if (isLoginError) {
        setIsLoggedIn(false);
        setAuthUser(null);
        setIsAuthGod(false);
        console.log(isLoginError);
      } else {
        setIsLoggedIn(false);
        setAuthUser(null);
        setIsAuthGod(false);
      }
    });
    return unsubscribe;
  }, [user, isLoginError]);

  async function clearFirestoreCache() {
    try {
      // First disable network
      await disableNetwork(firestore);
      // Add a small delay to ensure all listeners are cleaned up
      await new Promise((resolve) => setTimeout(resolve, 50));
      // Then re-enable
      await enableNetwork(firestore);
      console.log('Firestore cache cleared successfully.');
    } catch (error) {
      console.error('Error clearing Firestore cache:', error);
    }
  }

  // Listen for superUser document changes to determine isGod status and handle impersonation
  useEffect(() => {
    // Only listen if user is an auth god
    if (!isAuthGod || !user?.uid) {
      setIsGod(false);
      return;
    }

    const superUserRef = doc(firestore, 'superUser', user.uid);
    const unsubscribe = onSnapshot(
      superUserRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const superUserDoc = docSnapshot.data() as SuperUser;
          const godStatus = superUserDoc.isGod === true;
          const impersonate = superUserDoc.impersonate;
          setIsGod(godStatus);
          console.log('SuperUser doc loaded, isGod:', godStatus, 'impersonate:', impersonate);

          // When the syncImpersonationClaim trigger sets the custom claim, it writes
          // claimUpdatedAt. Force-refresh the token so Storage rules see the new claim.
          const claimUpdatedAt = superUserDoc.claimUpdatedAt instanceof Object
            ? (superUserDoc.claimUpdatedAt as { toMillis(): number }).toMillis()
            : null;
          if (claimUpdatedAt && claimUpdatedAt !== lastClaimUpdatedAtRef.current) {
            lastClaimUpdatedAtRef.current = claimUpdatedAt;
            user.getIdToken(true);
          }

          // Handle impersonation - update authUser.profileId if impersonating. A value that can't be
          // a profile ID (e.g. a pasted URL) is ignored rather than used as one, which would make
          // every profile path invalid and take the whole app down.
          const isValidImpersonation = !!impersonate && isValidDocumentId(impersonate.trim());
          if (impersonate && impersonate.trim() !== '' && !isValidImpersonation) {
            console.error('Ignoring impersonation value that is not a profile ID:', impersonate);
          }
          if (isValidImpersonation) {
            console.log('Impersonating user:', impersonate);
            setImpersonating(impersonate);
            setAuthUser({ user: user, profileId: impersonate });
          } else {
            // Not impersonating - use own profileId
            setImpersonating('');
            user.getIdTokenResult().then((tokenResult) => {
              const ownProfileId = (tokenResult?.claims?.profileId as string) || user.uid;
              console.log('No impersonation, using own profileId:', ownProfileId);
              setAuthUser({ user: user, profileId: ownProfileId });
            });
          }
        } else {
          // No superUser doc exists - default to false
          setIsGod(false);
          console.log('No superUser doc found, isGod: false');
        }
      },
      (error) => {
        console.error('Error listening to superUser doc:', error);
        setIsGod(false);
      }
    );

    return unsubscribe;
  }, [isAuthGod, user]);

  interface ReqCheckEmailInterface {
    email: string;
  }

  interface ResCheckEmailInterface {
    emailExists: boolean;
  }

  const checkUserExists = async (email: string): Promise<boolean> => {
    const checkAuthEmailExists = httpsCallable<ReqCheckEmailInterface, ResCheckEmailInterface>(
      cloudFunctions,
      'checkUserExists'
    );

    return checkAuthEmailExists({ email: email })
      .then((result) => {
        return result.data.emailExists;
      })
      .catch((error) => {
        console.log(error);
        return false;
      });
  };

  const signIn = async (email: string, password: string) => {
    signInWithEmailAndPassword(auth, email, password)
      .then((/* userCredential */) => {
        logAnalyticsEvent('login', { method: 'email' });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  async function signOutUser(): Promise<void> {
    try {
      // First sign out of Firebase Auth
      await signOut(auth);
      setAnalyticsUserId(null);

      // Clear react-firebase-hooks cache
      await clearFirestoreCache();

      // Clear local storage
      localStorage.clear();
      sessionStorage.clear();

      // Add a small delay to ensure all cleanup is complete
      await new Promise((resolve) => setTimeout(resolve, 100));
    } catch (error) {
      console.error('Error during sign out:', error);
      throw error;
    }
  }

  function resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(auth, email);
  }

  function sendVerifyEmailChange(continueUrl: string): Promise<void> {
    if (!user) {
      throw new Error('No user logged in');
    }
    const actionCodeSettings: ActionCodeSettings = {
      url: continueUrl,
    };
    return sendEmailVerification(user, actionCodeSettings);
  }

  const reauthenticateUser = useCallback(
    async (password: string) => {
      if (!user?.email) {
        throw new Error('No user email found');
      }
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
    },
    [user]
  );

  const updateUserEmail = useCallback(
    async (newEmail: string) => {
      if (!user) {
        throw new Error('No user logged in');
      }
      updateEmail(user, newEmail)
        .then(() => {
          console.log('Email updated');
        })
        .catch((error) => {
          console.error('Error updating email:', error);
        });
    },
    [user]
  );

  const updateUserPassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      if (!user) {
        throw new Error('No user logged in');
      }
      await reauthenticateUser(currentPassword);
      await updatePassword(user, newPassword);
    },
    [user, reauthenticateUser]
  );

  const values = {
    auth,
    authUser,
    authUrl,
    filesRef,
    impersonating,
    isAuthGod,
    isEmailVerified: user ? user.emailVerified : false,
    isGod,
    isLoggedIn,
    isLoggingIn,
    user: user ?? null,
    checkUserExists,
    reauthenticateUser,
    resetPassword,
    sendVerifyEmailChange,
    signIn,
    signOutUser,
    updateUserEmail,
    updateUserPassword,
  };
  return (
    <AuthContext.Provider value={values}>
      {isLoggingIn && <div>Loading...</div>}
      {children}
    </AuthContext.Provider>
  );
};

//export const useUserContext = (): UserContextState => {
//  return useContext(UserStateContext)
//}
